import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { ShowDialogProps } from '../../../show-dialog'
import { useZodForm } from '../../../hooks/zod-form'
import { invalidateGlobalDocument, useUpdateGlobalDocumentReadApproval } from '../../../api/global-documents'
import { AppForm, FormCheckbox, FormSubmitButton } from '../../../components/FormCore'
import { toast } from 'react-toastify'
import { CancelButton } from '../../../components/CancelButton'
import { invalidateDocument } from 'src/api/document'

export interface GlobalDocumentReadApprovalDialogProps extends ShowDialogProps {
  documentId: EntityId
  customerId?: EntityId
  isReadApprovalRequired: boolean
}

export const GlobalDocumentReadApprovalDialog: React.FC<GlobalDocumentReadApprovalDialogProps> = ({
  pop,
  documentId,
  customerId,
  isReadApprovalRequired,
}) => {
  const { t } = useTranslation(['global-documents', 'common'])

  const schema = z.object({
    id: z.number(),
    customerId: z.number(),
    isReadApprovalRequired: z.boolean(),
  })

  const form = useZodForm(schema, {
    defaultValues: {
      id: documentId,
      customerId: customerId ? Number(customerId) : undefined,
      isReadApprovalRequired: isReadApprovalRequired,
    },
  })

  const mutation = useUpdateGlobalDocumentReadApproval(documentId)

  const onSubmit = async form => {
    await mutation.mutateAsync(form).then(
      () => {
        invalidateGlobalDocument.approved()
        invalidateDocument.getDocumentsForApproval(form.customerId!)
        toast.success(t('updatedSuccessfully', { ns: 'common' }))
        pop()
      },
      () => {
        toast.error(t('updatingError', { ns: 'common' }))
      },
    )
  }

  return (
    <AppForm form={form} onSubmit={onSubmit}>
      <FormCheckbox
        name="isReadApprovalRequired"
        label={t('isReadApprovalRequired', { ns: 'document' })}
        defaultValue={isReadApprovalRequired}
      />
      <Box className="flex justify-end gap-4 pt-4">
        <CancelButton onClick={pop}>{t('cancel', { ns: 'common' })}</CancelButton>
        <FormSubmitButton createText={t('update', { ns: 'common' })} />
      </Box>
    </AppForm>
  )
}
